<template>
  <div
    class="tw-items-center tw-inline-flex"
    :class="`${
      isCursorPointer ? 'tw-w-[180px] tw-cursor-pointer' : 'tw-w-[180px]'
    }`"
  >
    <div
      v-if="value"
      :class="`${status[value]?.className} ${classCurrentUser}`"
      class="cell-status tw-flex tw-items-center"
      :data-cy="dataCy"
      @click="updateStatus(value)"
    >
      <span v-if="statusNumber" class="tw-mr-[3px]">{{
        props.statusNumber
      }}</span>
      <span
        :class="`tw-max-w-[115px] tw-truncate ${lableClass}`"
        data-cy="status-badge-lable"
        >{{ label ?? status[value]?.label }}</span
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import store from "@/store";
import {
  checklistRequestedStatusId,
  checklistStatusId,
  statusCode,
} from "@/utils/constants";
import utilities from "@/utils/utilities";
import { computed, onMounted, reactive } from "vue";

const props = defineProps({
  value: { type: String, required: true },
  isCursorPointer: { type: Boolean, default: false, required: false },
  statusNumber: { type: Number, default: () => 0 },
  label: { type: String, default: () => null },
  lableClass: { type: String, default: () => "" },
  dataCy: { type: String, default: () => "" },
  isCheckPlan: { type: Boolean, default: false },
  classCurrentUser: { type: String, default: "" },
});
const color = computed(() => (props.isCheckPlan ? "#904bd6" : "#878787"));
const borderColor = computed(() => (props.isCheckPlan ? "#eee4f9" : "#eff0f1"));
const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);
const emit = defineEmits(["update:status"]);

const updateStatus = (value) =>
  value == statusCode.pending ||
  (checklistStatusId.created && emit("update:status", value));
const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const status = reactive({});

onMounted(() => {
  switch (props.value) {
    case statusCode.active:
      status[statusCode.active] = {
        label: getUiText("app-component-table-cell-status-active-1"),
        className: "active",
      };
      break;
    case statusCode.invited:
      status[statusCode.invited] = {
        label: getUiText("app-component-table-cell-status-invited-1"),
        className: "invited",
      };
      break;
    case statusCode.deactivated:
      status[statusCode.deactivated] = {
        label: getUiText("app-component-table-cell-status-deactivated-1"),
        className: "deactivated",
      };
      break;
    case statusCode.pending:
      status[statusCode.pending] = {
        label: getUiText("app-component-table-cell-status-pending-1"),
        className: "pending",
      };
      break;
    case statusCode.critical:
      status[statusCode.critical] = {
        label: getUiText(
          "app-component-assessments-checklists-exposures-card-2",
        ),
        className: "critical",
      };
      break;
    case statusCode.high:
      status[statusCode.high] = {
        label: getUiText(
          "app-assess-recommendations-action-table-status-high-1",
        ),
        className: "high",
      };
      break;
    case statusCode.highest:
      status[statusCode.highest] = {
        label: getUiText(
          "app-assess-recommendations-action-table-status-highest-1",
        ),
        className: "significant",
      };
      break;
    case statusCode.moderate:
      status[statusCode.moderate] = {
        label: getUiText(
          "app-assess-recommendations-action-table-status-moderate-1",
        ),
        className: "moderate",
      };
      break;
    case statusCode.suggested:
      status[statusCode.suggested] = {
        label: getUiText(
          "app-assess-recommendations-action-table-status-suggested-1",
        ),
        className: "suggested",
      };
      break;
    case statusCode.applied:
      status[statusCode.applied] = {
        label: getUiText(
          "app-assess-recommendations-action-table-status-applied-1",
        ),
        className: "applied",
      };
      break;
    case statusCode.category:
      status[statusCode.category] = {
        label: "",
        className: "category",
      };
      break;
    case statusCode.subCategory:
      status[statusCode.subCategory] = {
        label: "",
        className: "subCategory",
      };
      break;
    case statusCode.locked:
      status[statusCode.locked] = {
        label: getUiText("app-component-table-cell-status-locked-1"),
        className: "critical",
      };
      break;
    case statusCode.verify:
      status[statusCode.verify] = {
        label: getUiText("app-component-table-cell-status-verified-1"),
        className: "active",
      };
      break;
    case statusCode.pendingVerify:
      status[statusCode.pendingVerify] = {
        label: getUiText("app-component-table-cell-status-pending-creation-1"),
        className: "pending",
      };
      break;
    case statusCode.notVerify:
      status[statusCode.notVerify] = {
        label: getUiText("app-component-table-cell-status-not-verified-1"),
        className: "deactivated",
      };
      break;
    case statusCode.archived:
      status[statusCode.archived] = {
        label: getUiText("app-component-table-cell-status-archived-1"),
        className: "deactivated",
      };
      break;
    case statusCode.disable:
      status[statusCode.disable] = {
        label: "",
        className: "disable",
      };
      break;
    case statusCode.system:
      status[statusCode.system] = {
        label: "",
        className: "System",
      };
      break;
    case statusCode.org:
      status[statusCode.org] = {
        label: "",
        className: "Org",
      };
      break;
    case statusCode.parent:
      status[statusCode.parent] = {
        label: "",
        className: "Parent",
      };
      break;
    case statusCode.required:
      status[statusCode.required] = {
        label: getUiText(
          "app-component-assessments-assessment-question-evidence-required-tag-1",
        ),
        className: "required",
      };
      break;
    case statusCode.accepted:
      status[statusCode.accepted] = {
        label: getUiText("app-component-status-badge-code-accepted-1"),
        className: "accepted",
      };
      break;
    case statusCode.informationRequired:
      status[statusCode.informationRequired] = {
        label: getUiText(
          "app-component-status-badge-code-information-required-1",
        ),
        className: "information-required",
      };
      break;
    case statusCode.notAccepted:
      status[statusCode.notAccepted] = {
        label: getUiText("app-component-status-badge-code-not-accepted-1"),
        className: "not-accepted",
      };
      break;
    case statusCode.inProgress:
      status[statusCode.inProgress] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-1",
        ),
        className: "inProgress",
      };
      break;
    case statusCode.notStarted:
      status[statusCode.notStarted] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-5",
        ),
        className: "notStarted",
      };
      break;
    case statusCode.complete:
      status[statusCode.complete] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-3",
        ),
        className: "complete",
      };
      break;
    case statusCode.created:
      status[statusCode.created] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-label-1",
        ),
        className: "notStarted",
      };
      break;
    case statusCode.completed:
      status[statusCode.completed] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-3",
        ),
        className: "completed",
      };
      break;
    case statusCode.waitingOnUs:
      status[statusCode.waitingOnUs] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-5",
        ),
        className: "onHold",
      };
      break;
    case statusCode.waiting:
      status[statusCode.waiting] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-5",
        ),
        className: "onHold",
      };
      break;
    case statusCode.paused:
      status[statusCode.paused] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-5",
        ),
        className: "onHold",
      };
      break;
    case statusCode.other:
      status[statusCode.other] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-5",
        ),
        className: "onHold",
      };
      break;
    case statusCode.audited:
      status[statusCode.audited] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-4",
        ),
        className: "audited",
      };
      break;
    case statusCode.defunct:
      status[statusCode.defunct] = {
        label: getUiText(
          "app-component-assessments-checklists-assessment-details-card-status-3",
        ),
        className: "defunct",
      };
      break;
    case statusCode.vendorSubmitted:
      status[statusCode.vendorSubmitted] = {
        label: getUiText(
          "app-component-status-badge-vendor-assessment-submitted-status-1",
        ),
        className: "vendorSubmitted",
      };
      break;
    case statusCode.waitingForVendor:
      status[statusCode.waitingForVendor] = {
        label: getUiText(
          "app-component-status-badge-vendor-assessment-waiting-status-1",
        ),
        className: "waitingForVendor",
      };
      break;
    case statusCode.wait:
      status[statusCode.wait] = {
        label: getUiText(
          "app-component-status-badge-vendor-assessment-wait-status-1",
        ),
        className: "wait",
      };
      break;
    case statusCode.inProg:
      status[statusCode.inProg] = {
        label: getUiText(
          "app-component-status-badge-vendor-assessment-in-progress-status-1",
        ),
        className: "inProgress",
      };
      break;
    case checklistRequestedStatusId.created:
      status[checklistRequestedStatusId.created] = {
        label: getUiText(
          "app-component-assessments-checklists-requested-status-created-1",
        ),
        className: "checkListRequestedCreated",
      };
      break;
    case checklistStatusId.inProgress:
      status[checklistStatusId.inProgress] = {
        label: getUiText(
          "app-component-assessments-checklist-requested-status-inprogress-1",
        ),
        className: "checkListRequestedInprogress",
      };
      break;
    case checklistStatusId.waiting:
      status[checklistStatusId.waiting] = {
        label: getUiText(
          "app-component-assessments-checklist-requested-status-waiting-1",
        ),
        className: "checkListRequestedWait",
      };
      break;
    case checklistStatusId.submitted:
      status[checklistStatusId.submitted] = {
        label: getUiText(
          "app-component-status-badge-vendor-assessment-submitted-status-1",
        ),
        className: "checkListRequestedSubmited",
      };
      break;
    case checklistStatusId.completed:
      status[checklistStatusId.completed] = {
        label: getUiText(
          "app-component-assessments-checklists-requested-status-completed-1",
        ),
        className: "checkListRequestedCompleted",
      };
      break;
    default:
      break;
  }
});
</script>

<style scoped lang="scss">
.cell-status {
  padding: 3px 5px;
  height: 20px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.active {
  background-color: #d9ede2;
  color: #01893d;
}

.invited {
  background-color: #e4f1ff;
  color: #0345bd;
}

.required {
  background-color: #fee5e2;
  color: #f7553c;
}

.pending {
  background-color: #e4f1ff;
  color: #0345bd;
}

.deactivated {
  background: var(
    --ui-background,
    linear-gradient(45deg, #f1f2f3 0%, #eeeff0 100%)
  );
  color: #b7b7b7;
}

.critical {
  background: linear-gradient(
      0deg,
      rgba(247, 85, 60, 0.15) 0%,
      rgba(247, 85, 60, 0.15) 100%
    ),
    #fff;
  color: #f7553c;
}
.high {
  background: rgba(144, 75, 214, 0.1);
  color: rgba(144, 75, 214, 1);
}
.significant {
  background: rgba(144, 75, 214, 1);
  color: rgba(255, 255, 255, 1);
}
.moderate {
  background: #fff;
  color: #904bd6;
  border: 1px solid #eee4f9;
}
.suggested {
  background: rgba(255, 255, 255, 1);
  color: rgba(254, 154, 5, 1);
  border: 1px solid rgba(254, 154, 5, 0.15);
}
.applied {
  background: rgba(255, 255, 255, 1);
  color: rgba(1, 137, 61, 1);
  border: 1px solid rgba(1, 137, 61, 0.15);
}
.category {
  background: var(--Brand-Dark-Sky, #387dff);
  color: var(--UI-white, #fff);
}
.subCategory {
  background: var(--UI-light-sky-soft, #e4f1ff);
  color: var(--Brand-Dark-Sky, #387dff);
}
.disable {
  background: var(--UI-light-sky-soft, #dbdbdb);
  color: var(--Brand-Dark-Sky, #ffffff);
}
.System {
  border: 1px solid v-bind(borderColor);
  color: v-bind(color);
  background: #fff;
  line-height: 130%;
}
.Org {
  border: 1px solid #d9ede2;
  background: #fff;
  color: #01893d;
  line-height: 130%;
}
.Parent {
  border: 1px solid #e4f1ff;
  background: #fff;
  color: #387dff;
  line-height: 130%;
}
.accepted {
  background: #d9ede2;
  color: #01893d;
  font-weight: 500;
  line-height: 130%;
}
.information-required {
  color: #fe9a05;
  font-weight: 500;
  line-height: 130%;
  background: var(
    --placeholder-orange-soft,
    linear-gradient(
      0deg,
      rgba(254, 154, 5, 0.15) 0%,
      rgba(254, 154, 5, 0.15) 100%
    ),
    #fff
  );
}
.not-accepted {
  background: #fee5e2;
  color: #f7553c;
  font-weight: 500;
  line-height: 130%;
}
.created {
  border: 1px solid #d9ede2;
  background: #fff;
  color: #f7553c;
  line-height: 130%;
}
.inProgress {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}

.notStarted {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.completed {
  background: var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.audited {
  background: linear-gradient(
      0deg,
      rgba(144, 75, 214, 0.15) 0%,
      rgba(144, 75, 214, 0.15) 100%
    ),
    #fff;
  color: #904bd6;
}

.onHold {
  background: #fff0da;
  color: #fe9a05;
}

.Defunct {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.Created {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}
</style>
